import { systemContentKey } from "./dtoolsProposalContent";
import type {
  DtoolsSystemClassificationEntry,
  DtoolsSystemClassificationsMap,
} from "./dtoolsSystemClassification";
import { resolveCustomerFacingSystemTitle } from "./dtoolsSystemClassification";
import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";

export type DtoolsProposalDisclaimer = {
  id: string;
  name: string;
  content: string;
  /** Optional library template reference — content is frozen at copy time. */
  templateId?: number | null;
  sortOrder: number;
};

export const DEFAULT_SYSTEM_SUMMARY_EXECUTIVE_OVERVIEW =
  "This proposal brings together the systems and services recommended for your project. The overview below explains what we are proposing to achieve, which systems are included, and how they contribute to the overall proposal before you review each system in detail.";

/** @deprecated Use DEFAULT_SYSTEM_SUMMARY_EXECUTIVE_OVERVIEW */
export const DEFAULT_SYSTEM_SUMMARY_INTRODUCTION = DEFAULT_SYSTEM_SUMMARY_EXECUTIVE_OVERVIEW;

export type DtoolsProposalPresentation = {
  proposalTitle?: string | null;
  proposalSubtitle?: string | null;
  customerIntroduction?: string | null;
  customerIntroductionLastAiPolishedAt?: string | null;
  /** Executive overview shown on the System Summary customer tab. */
  systemSummaryExecutiveOverview?: string | null;
  /** @deprecated Renamed to systemSummaryExecutiveOverview */
  systemSummaryIntroduction?: string | null;
  heroImageUrl?: string | null;
  /** Maps system content key → customer-facing option label. */
  systemDisplayNames?: Record<string, string>;
  proposalDisclaimers?: DtoolsProposalDisclaimer[];
};

export type ProposalPresentationHeaderInput = {
  proposalName?: string | null;
  proposalRef?: string | null;
  customerNameSnapshot?: string | null;
  projectNumber?: string | null;
  linkBExpiresAt?: string | Date | null;
};

export function readDtoolsProposalPresentation(parsedMetadataJson: unknown): DtoolsProposalPresentation {
  if (!parsedMetadataJson || typeof parsedMetadataJson !== "object" || Array.isArray(parsedMetadataJson)) {
    return {};
  }
  const raw = (parsedMetadataJson as Record<string, unknown>).dtoolsProposalPresentation;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const r = raw as Record<string, unknown>;
  const systemDisplayNames =
    r.systemDisplayNames && typeof r.systemDisplayNames === "object" && !Array.isArray(r.systemDisplayNames)
      ? Object.fromEntries(
          Object.entries(r.systemDisplayNames as Record<string, unknown>).map(([k, v]) => [
            k,
            String(v ?? ""),
          ]),
        )
      : undefined;
  const proposalDisclaimers = parseProposalDisclaimers(r.proposalDisclaimers);
  return {
    proposalTitle: typeof r.proposalTitle === "string" ? r.proposalTitle : r.proposalTitle === null ? null : undefined,
    proposalSubtitle:
      typeof r.proposalSubtitle === "string" ? r.proposalSubtitle : r.proposalSubtitle === null ? null : undefined,
    customerIntroduction:
      typeof r.customerIntroduction === "string"
        ? r.customerIntroduction
        : r.customerIntroduction === null
          ? null
          : undefined,
    customerIntroductionLastAiPolishedAt:
      typeof r.customerIntroductionLastAiPolishedAt === "string" ? r.customerIntroductionLastAiPolishedAt : undefined,
    systemSummaryExecutiveOverview:
      typeof r.systemSummaryExecutiveOverview === "string"
        ? r.systemSummaryExecutiveOverview
        : r.systemSummaryExecutiveOverview === null
          ? null
          : undefined,
    systemSummaryIntroduction:
      typeof r.systemSummaryIntroduction === "string"
        ? r.systemSummaryIntroduction
        : r.systemSummaryIntroduction === null
          ? null
          : undefined,
    heroImageUrl: typeof r.heroImageUrl === "string" ? r.heroImageUrl : r.heroImageUrl === null ? null : undefined,
    systemDisplayNames,
    proposalDisclaimers,
  };
}

function parseProposalDisclaimers(raw: unknown): DtoolsProposalDisclaimer[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: DtoolsProposalDisclaimer[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const row = entry as Record<string, unknown>;
    const id = String(row.id ?? "").trim();
    const name = String(row.name ?? "").trim();
    const content = String(row.content ?? "").trim();
    if (!id || !name) continue;
    const templateId = Number(row.templateId);
    out.push({
      id,
      name,
      content,
      templateId: Number.isFinite(templateId) && templateId > 0 ? templateId : null,
      sortOrder: Number.isFinite(Number(row.sortOrder)) ? Number(row.sortOrder) : out.length,
    });
  }
  return out.sort((a, b) => a.sortOrder - b.sortOrder);
}

export function mergeDtoolsProposalPresentation(
  parsedMetadataJson: unknown,
  patch: Partial<DtoolsProposalPresentation>,
): Record<string, unknown> {
  const base =
    parsedMetadataJson && typeof parsedMetadataJson === "object" && !Array.isArray(parsedMetadataJson)
      ? { ...(parsedMetadataJson as Record<string, unknown>) }
      : {};
  const existing = readDtoolsProposalPresentation(base);
  return {
    ...base,
    dtoolsProposalPresentation: { ...existing, ...patch },
  };
}

export function normalizeSystemSummaryExecutiveOverview(value: unknown): string | null {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized || normalized === DEFAULT_SYSTEM_SUMMARY_EXECUTIVE_OVERVIEW) return null;
  return normalized;
}

export function resolveSystemSummaryExecutiveOverview(
  presentation?: DtoolsProposalPresentation,
): string | null {
  return (
    normalizeSystemSummaryExecutiveOverview(presentation?.systemSummaryExecutiveOverview) ??
    normalizeSystemSummaryExecutiveOverview(presentation?.systemSummaryIntroduction)
  );
}

/** @deprecated Use resolveSystemSummaryExecutiveOverview */
export function resolveSystemSummaryIntroduction(
  presentation?: DtoolsProposalPresentation,
): string | null {
  return resolveSystemSummaryExecutiveOverview(presentation);
}

export function resolveProposalTitle(
  header: ProposalPresentationHeaderInput,
  presentation?: DtoolsProposalPresentation,
): string {
  const custom = presentation?.proposalTitle?.trim();
  if (custom) return custom;
  return String(header.proposalName ?? header.proposalRef ?? "Proposal").trim() || "Proposal";
}

export function resolveProposalSubtitle(
  header: ProposalPresentationHeaderInput,
  presentation?: DtoolsProposalPresentation,
): string | null {
  const custom = presentation?.proposalSubtitle?.trim();
  if (custom) return custom;
  const customer = String(header.customerNameSnapshot ?? "").trim();
  if (customer) return `Prepared for ${customer}`;
  const ref = String(header.projectNumber ?? header.proposalRef ?? "").trim();
  return ref || null;
}

export function defaultOptionLabel(optionIndex: number): string {
  return `Option ${optionIndex + 1}`;
}

export function resolveSystemDisplayName(
  system: Pick<DtoolsReviewSystem, "systemId" | "name">,
  optionIndex: number,
  presentation?: DtoolsProposalPresentation,
  classification?: DtoolsSystemClassificationEntry,
): string {
  return resolveCustomerFacingSystemTitle(system, {
    classification,
    presentationSystemDisplayNames: presentation?.systemDisplayNames,
    optionIndex,
  });
}

export function buildSystemDisplayNameMap(
  systems: DtoolsReviewSystem[],
  presentation?: DtoolsProposalPresentation,
  classifications?: DtoolsSystemClassificationsMap,
): Map<string, string> {
  const map = new Map<string, string>();
  systems.forEach((system, index) => {
    const key = systemContentKey(system.systemId, system.name);
    map.set(
      key,
      resolveCustomerFacingSystemTitle(system, {
        classification: classifications?.[key],
        presentationSystemDisplayNames: presentation?.systemDisplayNames,
        optionIndex: index,
      }),
    );
  });
  return map;
}

export function copyDisclaimerFromTemplate(
  template: { id: number; name: string; content: string },
  sortOrder: number,
): DtoolsProposalDisclaimer {
  return {
    id: `pd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    name: template.name,
    content: template.content,
    templateId: template.id,
    sortOrder,
  };
}

export function reorderProposalDisclaimers(
  disclaimers: DtoolsProposalDisclaimer[],
  fromIndex: number,
  toIndex: number,
): DtoolsProposalDisclaimer[] {
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= disclaimers.length || toIndex >= disclaimers.length) {
    return disclaimers;
  }
  const next = [...disclaimers];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return disclaimers;
  next.splice(toIndex, 0, moved);
  return next.map((row, idx) => ({ ...row, sortOrder: idx }));
}

export function removeProposalDisclaimer(
  disclaimers: DtoolsProposalDisclaimer[],
  id: string,
): DtoolsProposalDisclaimer[] {
  return disclaimers.filter((d) => d.id !== id).map((row, idx) => ({ ...row, sortOrder: idx }));
}

export function updateProposalDisclaimer(
  disclaimers: DtoolsProposalDisclaimer[],
  id: string,
  patch: Partial<Pick<DtoolsProposalDisclaimer, "name" | "content">>,
): DtoolsProposalDisclaimer[] {
  return disclaimers.map((row) => (row.id === id ? { ...row, ...patch } : row));
}

export function sortedProposalDisclaimers(
  presentation?: DtoolsProposalPresentation,
): DtoolsProposalDisclaimer[] {
  return [...(presentation?.proposalDisclaimers ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
}
